import React, { useCallback } from "react";
import Grid from "@mui/material/Grid2";
import {
  AI_TROUBLESHOOT_EVENT_SUBJECT,
  Ticket,
  TicketEvent,
  TicketStatus,
} from "@services/tickets/types";
import {
  convertToUserTimeZone,
  displayDateTime,
} from "@common/utils/localTime.utils";
import { Avatar, Box, IconButton, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import ConsoleLog from "@pages/Tickets/ConsoleLog";
import Typography from "@common/components/Typography";
import Chip from "@common/components/Chips";
import { Add } from "@mui/icons-material";
import { useContext, useState } from "react";
import AddEventDialog from "../../AddEventDialog";
import { FormContext } from "../../context/formContext";
import TroubleshootingDialog from "@pages/Assets/Device/TroubleshootingDialog";
import { Priority } from "@services/common/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import ReactMarkdown from "react-markdown";
import { getTicket, putTicket } from "@services/tickets";
import {
  NotificationContext,
  useErrorNotification,
} from "@common/components/Notification";
import { hasLicense } from "@common/utils/user";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import { mapToCSVData } from "@common/components/CSV/helpers";
import { Data as CSVData } from "@common/components/CSV/types";
import { CSVLoader } from "@common/components/CSV/CSVLoader";
import { getColumns, getLast100 } from "./const";
import { CSV } from "@assets/icons";

const AVATAR_SIZE = "2rem";
const EFFECT_SIZE = "4px";

const Timeline = () => {
  const { t } = useTranslation();
  const [user] = useAuthenticatedUser();
  const [troubleshoot, setTroubleshoot] = useState<string | undefined>();
  const { form, canEdit, dispatchDetails, dispatchHasChanged } =
    useContext(FormContext);
  const { ticket, details } = form;
  const [openAddEventDialog, setOpenAddEventDialog] = useState<boolean>(false);
  const [openCSVLoader, setOpenCSVLoader] = useState<undefined | CSVData>();
  const [, , accounts] = useUserSelectedAccounts();
  const dispatchError = useErrorNotification();
  const { dispatchNotification } = useContext(NotificationContext);

  const disabled = ticket?.status == "Closed" || !canEdit;
  const canAITroubleshoot = hasLicense(
    accounts.find((acc) => acc.id === ticket.account?.id),
    "aiTroubleshooting"
  );
  const canExportTimeline = Boolean(ticket?.id);
  const ticketTimeZone =
    convertToUserTimeZone(
      ticket?.device?.room?.location?.details?.timeZone ??
        ticket?.room?.location?.details?.timeZone
    ) ?? undefined;

  const openTroubleshoot = useCallback(
    async (evt: string) => {
      setTroubleshoot(evt);
      const timestamp = new Date().getTime();
      /**
       * Let's save a note that we opened the AI troubleshoot, without any collateral damage.
       */
      try {
        if (ticket.id) {
          const newNote = {
            note: `Ran AI Troubleshooting on "${evt}"`,
            creator: user?.email,
            createDate: timestamp,
          };
          let t = await getTicket(ticket.id as unknown as string);
          putTicket({
            ...t,
            details: {
              ...t.details,
              notes: [...(t.details?.notes ?? []), newNote],
            },
          });
          t = await getTicket(t.id as unknown as string);
          const matchingNote = t.details?.notes?.find(
            (x) => x.createDate === timestamp
          );
          if (matchingNote) {
            const newDetails = {
              ...details,
              notes: [...(details.notes ?? []), matchingNote],
            };
            dispatchDetails(newDetails);
          }
        }
      } catch (e) {
        dispatchError(e);
      }
    },
    [details]
  );

  const displayTimelineContent = useCallback(() => {
    if (details && details.events) {
      return details.events
        .sort(({ createDate: a }, { createDate: b }) => Math.sign(b - a))
        .map((event: TicketEvent, index: number) => {
          return (
            <Grid key={`lineTimeline-${index}`}>
              <Grid container spacing={2} sx={{ width: "100%" }}>
                <Grid
                  sx={{
                    width: "10rem",
                    textAlign: "center",
                  }}
                >
                  <ConsoleLog>
                    <Typography realTime>
                      {displayDateTime(event.createDate)}
                    </Typography>
                  </ConsoleLog>
                </Grid>
                {ticketTimeZone && (
                  <Grid
                    sx={{
                      width: "10rem",
                      textAlign: "center",
                    }}
                  >
                    <ConsoleLog>
                      <Typography realTime>
                        {displayDateTime(event.createDate, ticketTimeZone)}
                      </Typography>
                    </ConsoleLog>
                  </Grid>
                )}
                <Grid>
                  <Chip
                    radius="small"
                    color={event.priority}
                    variant="outlined"
                    label={event.priority}
                    uppercase={false}
                    realTime
                  />
                </Grid>
                <Grid
                  sx={[
                    {
                      flex: "1",
                      fontFamily: "realTime",
                    },
                    event.subject === AI_TROUBLESHOOT_EVENT_SUBJECT
                      ? { "> p:first-of-type": { mt: 0 } }
                      : {},
                  ]}
                >
                  {event.subject === AI_TROUBLESHOOT_EVENT_SUBJECT ? (
                    <ReactMarkdown
                      components={{
                        a: ({ node, href, children, ...props }) => (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          >
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {event.description ?? ""}
                    </ReactMarkdown>
                  ) : (
                    <Typography realTime>{event.description}</Typography>
                  )}
                </Grid>
                {event.priority !== Priority.Informational &&
                  ticket.device?.id !== undefined &&
                  ticket.status !== TicketStatus.Closed &&
                  canAITroubleshoot && (
                    <Box sx={{ transform: "translateX(0)" }}>
                      <Avatar
                        sx={({ palette }) => ({
                          height: AVATAR_SIZE,
                          width: AVATAR_SIZE,
                          color: palette.text.primary,
                          borderColor: palette.error.main,
                          backgroundColor: palette.error.main,
                          "&:hover": {
                            cursor: "pointer",
                            backgroundColor: palette.error.dark,
                          },
                        })}
                        onClick={() =>
                          openTroubleshoot(event.description ?? "")
                        }
                      >
                        AI
                      </Avatar>
                      <Box
                        sx={({ palette }) => ({
                          backgroundImage: `conic-gradient(transparent 60deg,${palette.error.dark} 120deg)`,
                          height: `calc(${AVATAR_SIZE} + 2 * ${EFFECT_SIZE})`,
                          width: `calc(${AVATAR_SIZE} + 2 * ${EFFECT_SIZE})`,
                          borderRadius: "100%",
                          position: "absolute",
                          animation: "rotate 2s infinite linear",
                          zIndex: "-1",
                          top: `-${EFFECT_SIZE}`,
                          left: `-${EFFECT_SIZE}`,
                          "@keyframes rotate": {
                            "100%": {
                              transform: "rotate(360deg)",
                            },
                          },
                        })}
                      />
                    </Box>
                  )}
              </Grid>
            </Grid>
          );
        });
    } else return <></>;
  }, [details]);

  const getCSVData = useCallback(() => {
    setOpenCSVLoader(
      mapToCSVData(
        getLast100(details.events ?? []),
        getColumns(t, ticketTimeZone),
        // Should never be undefined, but you never know.
        `${ticket.id ?? "ticketid"}.csv`,
        undefined
      )
    );
  }, [ticket.id, details, ticketTimeZone]);

  return (
    <>
      <Grid
        container
        sx={(theme) => ({
          padding: theme.spacing(2, 4),
          flexDirection: "column",
        })}
        spacing={2}
      >
        <Grid>
          <Grid
            container
            spacing={2}
            sx={{ width: "100%", flexDirection: "row" }}
          >
            <Grid>{t("Ticket Event Timeline")}</Grid>
            <Tooltip title={t("Add") as string} arrow>
              <span>
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => setOpenAddEventDialog(true)}
                  disabled={disabled}
                  sx={{
                    marginTop: 1.5,
                    marginLeft: 0.75,
                  }}
                >
                  <Add />
                </IconButton>
              </span>
            </Tooltip>
            {ticket.id && (
              <>
                <Tooltip title={t("Export Last 100") as string} arrow>
                  <span>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => getCSVData()}
                      disabled={!canExportTimeline}
                      sx={{
                        marginTop: 1.5,
                        marginLeft: 0.75,
                      }}
                    >
                      <CSV />
                    </IconButton>
                  </span>
                </Tooltip>
                <CSVLoader
                  loading={false}
                  data={openCSVLoader}
                  resetData={() => setOpenCSVLoader(undefined)}
                />
              </>
            )}
          </Grid>
        </Grid>
        <Grid>
          <Grid
            container
            spacing={1}
            sx={{ width: "100%", flexDirection: "column" }}
          >
            {ticketTimeZone && (
              <Grid key="lineTimeline-header">
                <Grid container spacing={2} sx={{ width: "100%" }}>
                  <Grid
                    sx={{
                      width: "10rem",
                      textAlign: "center",
                      fontSize: "smaller",
                    }}
                  >
                    <Tooltip
                      title={t(user?.details?.timeZone ?? "")}
                      arrow
                      placement="top"
                    >
                      {t("User Time")}
                    </Tooltip>
                  </Grid>
                  <Grid
                    sx={{
                      width: "10rem",
                      textAlign: "center",
                      fontSize: "smaller",
                    }}
                  >
                    <Tooltip
                      title={t(
                        ticket?.device?.room?.location?.details?.timeZone ??
                          ticket?.room?.location?.details?.timeZone ??
                          ""
                      )}
                      arrow
                      placement="top"
                    >
                      {t("Source Time")}
                    </Tooltip>
                  </Grid>
                </Grid>
              </Grid>
            )}
            {displayTimelineContent()}
          </Grid>
        </Grid>
      </Grid>
      <TroubleshootingDialog
        setTroubleshoot={setTroubleshoot}
        troubleshoot={troubleshoot}
        device={ticket.device}
        ticket={ticket as Ticket}
        addToTimeline={(message) => {
          dispatchDetails({
            ...details,
            events: [
              ...(details.events ?? []),
              {
                createDate: new Date().getTime(),
                description: message,
                subject: AI_TROUBLESHOOT_EVENT_SUBJECT,
                priority: Priority.Informational,
              },
            ],
          });
          dispatchHasChanged(true);
          dispatchNotification({
            title: t("Success"),
            message: t(
              "Troubleshooting steps have been added to the Ticket Event Timeline successfully."
            ),
            severity: "success",
          });
        }}
      />
      <AddEventDialog
        open={openAddEventDialog}
        onClose={() => setOpenAddEventDialog(false)}
      ></AddEventDialog>
    </>
  );
};

export default Timeline;
